import { useEffect, useMemo, useState } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import {
  deleteAutoSwitch,
  fetchAllProviders,
  registerAutoSwitchProvider,
  updateAutoSwitchProvider,
} from "../../../config/LiveApi";
import { useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import { id } from "date-fns/locale";

const MerchantDetailTable = ({
  openId,
  userId,
  setOpenId,
  isEditing,
  merchantName,
  isAdding,
  setIsAdding,
  data,
  getProviderNameById,
  setExpandedMerchants,
  setChange,
  change
}) => {
  const [selectedProviders, setSelectedProviders] = useState({});
  const [updatedPriorities, setUpdatedPriorities] = useState({});
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [localData, setLocalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const [providersName, setProvidersName] = useState([]);
  const [newProvider, setNewProvider] = useState(null);
  const [newPaymentMethod, setNewPaymentMethod] = useState(null);

  const token = useSelector((store) => store.userReducer.token);
  const sortedData = useMemo(() => {
    const collect = data
      .filter((d) => d.paymentMethod === "UPI_COLLECT")
      .sort((a, b) => {
        const prioA = isEditing
          ? updatedPriorities[a.id] ?? Infinity
          : a.priority ?? Infinity;
        const prioB = isEditing
          ? updatedPriorities[b.id] ?? Infinity
          : b.priority ?? Infinity;
        return prioA - prioB;
      });

    const intent = data
      .filter((d) => d.paymentMethod === "UPI_INTENT")
      .sort((a, b) => {
        const prioA = isEditing
          ? updatedPriorities[a.id] ?? Infinity
          : a.priority ?? Infinity;
        const prioB = isEditing
          ? updatedPriorities[b.id] ?? Infinity
          : b.priority ?? Infinity;
        return prioA - prioB;
      });

    return [...collect, ...intent];
  }, [data, updatedPriorities, isEditing]);

  const getFilteredOptions = (paymentMethod) => {
    let filteredProviders = providersName.filter((p) => p.type === "payin");

    if (paymentMethod === "UPI_INTENT") {
      filteredProviders = providersName.filter((p) => p.mode === "UPI_INTENT");
    }
    if (paymentMethod === "UPI_COLLECT") {
      filteredProviders = providersName.filter((p) => p.mode === "UPI_COLLECT");
    }

    const providerOptionsFiltered = filteredProviders.map((p) => ({
      label: `${p.providerName} - ${p.orgName}`,
      value: p.id,
      providerId: p.id,
      providerName: p.providerName,
      orgName: p.orgName,
    }));

    const groupedOptionsFiltered = [
      {
        label: "Payin",
        options: providerOptionsFiltered,
      },
    ];

    return groupedOptionsFiltered;
  };

  const handleSelectChangePayout = (selectedOption, rowId) => {
    setSelectedProviders((prev) => ({
      ...prev,
      [rowId]: selectedOption.providerId,
    }));
  };

  const getPriorityOptions = (mode) => {
    const filtered = sortedData.filter((d) => d.paymentMethod === mode);
    return Array.from({ length: filtered.length }, (_, i) => ({
      label: (i + 1).toString(),
      value: i + 1,
    }));
  };

  useEffect(() => {
    const fetchMerchDetails = async () => {
      if ((isEditing && openId) || isAdding) {
        try {
          const response = await fetchAllProviders(token);
          const providerName = response.data.providers;
          setProvidersName(providerName);
          setLoading(false);
        } catch (error) {
          setLoading(false);
        }
      }
    };

    fetchMerchDetails();
  }, [openId, token]);

  useEffect(() => {
    if (providersName.length && data.length) {
      const initialSelected = {};
      const initialPriorities = {};

      data.forEach((ip) => {
        const filtered = getFilteredOptions(ip.paymentMethod)[0]?.options || [];

        const matched = filtered.find(
          (p) => p.providerId === ip.providerId && p.orgName === ip.orgName
        );

        if (matched) {
          initialSelected[ip.id] = matched.providerId;
        }

        if (ip.priority) {
          initialPriorities[ip.id] = ip.priority;
        }
      });

      setSelectedProviders(initialSelected);
      setUpdatedPriorities(initialPriorities);
    }
  }, [providersName, data]);

  const handleUpdateAutoSwitch = async () => {
    try {
      const payload = Object.entries(selectedProviders)
        .map(([rowId, providerId]) => ({
          id: rowId,
          providerId,
          priority: updatedPriorities[rowId],
        }))
        .filter((entry) => entry.priority != null);

      await updateAutoSwitchProvider(token, payload);
      setChange(change+1);
      handleClose();
      toast.success("Auto switch updated successfully");
    } catch (error) {
      toast.error("Something went wrong while updating auto switch");
    }
  };

  const handleClose = () => {
    setExpandedMerchants((prev) => ({
      ...prev,
      [userId]: false,
    }));
    setOpenId(null);
    setIsAdding(false);
  };

  const handleDelete = async (id) => {
    try {
      const response = await deleteAutoSwitch(token, id);
      toast.success("Auto switch deleted successfully");
      handleClose();
      setChange(change+1);
    } catch (error) {
      toast.error("Someting went wrong while deleting auto switch");
    }
  };
  const handleAddSwitch = async () => {
    if (!newProvider || !newPaymentMethod) {
      toast.error("Please select both provider and payment method.");
      return;
    }

    const priority =
      Math.max(
        ...data
          .filter((d) => d.paymentMethod === newPaymentMethod.value)
          .map((d) => d.priority || 0),
        0
      ) + 1;

    try {
      const payload = {
        mode: [newPaymentMethod.value],
        successRate: 100,
        newPriority: priority,
        switchData: {
          [newPaymentMethod.value]: [newProvider.providerId],
        },
        userId,
      };

      await registerAutoSwitchProvider(token, payload);
      toast.success("Auto switch added successfully");
      setIsAdding(false);
      setChange(change+1);
      handleClose();
    } catch (error) {
      toast.error("Something went wrong while adding auto switch");
    }
  };

  useEffect(() => {
    setNewProvider(null);
  }, [newPaymentMethod]);

  useEffect(() => {
    if (isEditing) {
      const dataWithPriorities = [...sortedData].map((item, index) => ({
        ...item,
        priority: item.priority ?? index + 1,
      }));
      setLocalData(dataWithPriorities);
    }
  }, [isEditing, sortedData]);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragEnter = (targetIndex) => {
    if (draggedIndex === null || targetIndex === draggedIndex) return;

    const reordered = [...localData];
    const [draggedItem] = reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, draggedItem);

    // Reassign priorities
    const updatedPriorities = {};
    const updatedData = reordered.map((item, idx) => {
      updatedPriorities[item.id] = idx + 1;
      return { ...item, priority: idx + 1 };
    });

    setLocalData(updatedData);
    setUpdatedPriorities(updatedPriorities);
    setDraggedIndex(targetIndex);
    setHoveredIndex(targetIndex);
  };

  const handleDrop = () => {
    setDraggedIndex(null);
    setHoveredIndex(null);
  };

  return (
    <>
      {loading && isEditing ? (
        <p>Loading...</p>
      ) : (
        <table className="inner-detail-table">
          <thead>
            <tr>
              <th>Priority</th>
              <th>Provider Details</th>
              <th>Payment Mode</th>
              <th>Remove</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((ip, index) => {
              const isDragging = index === draggedIndex;
              const isHovered = index === hoveredIndex;
              return (
                <tr
                  key={ip.id}
                  className={`merchant-detail-row ${
                    isDragging ? "dragging" : ""
                  } ${isHovered ? "hovered" : ""}`}
                  draggable = {isEditing}
                  onDragStart={() => handleDragStart(index)}
                  onDragEnter={() => handleDragEnter(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  style={{
                    transform: isDragging ? "scale(1.02)" : "none",
                    zIndex: isDragging ? 1000 : "auto",
                    transition: "transform 200ms ease",
                    backgroundColor: isDragging
                      ? "#f0f0f0"
                      : isHovered
                      ? "#e9f7ff"
                      : "white",
                  }}
                >
                  <td>{index + 1}</td>
                  <td className="provider-details">
                    {isEditing && !isAdding ? (
                      <Select
                        value={(() => {
                          const options =
                            getFilteredOptions(ip.paymentMethod)[0]?.options ||
                            [];
                          return (
                            options.find(
                              (opt) =>
                                opt.providerId === selectedProviders[ip.id]
                            ) || null
                          );
                        })()}
                        onChange={(selectedOption) =>
                          handleSelectChangePayout(selectedOption, ip.id)
                        }
                        options={getFilteredOptions(ip.paymentMethod)}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    ) : (
                      getProviderNameById(ip.providerId) + " : " + ip.orgName ||
                      "N/A"
                    )}
                  </td>
                  <td>{ip.paymentMethod || "N/A"}</td>
                  <td>
                    <button className="del-btn">
                      <FaTrash size={19} onClick={() => handleDelete(ip.id)} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {isAdding && (
              <tr className="merchant-detail-row">
                <td>
                  {Math.max(
                    ...data
                      .filter(
                        (p) => p.paymentMethod === newPaymentMethod?.value
                      )
                      .map((d) => d.priority || 0),
                    0
                  ) + 1}
                </td>
                <td className="provider-details">
                  <Select
                    value={newProvider}
                    onChange={(selected) => setNewProvider(selected)}
                    options={(() => {
                      if (newPaymentMethod === null) {
                        return [];
                      }
                      let filtered = providersName.filter(
                        (p) => p.type === "payin"
                      );
                      if (newPaymentMethod?.value === "UPI_INTENT") {
                        filtered = filtered.filter(
                          (p) => p.mode === "UPI_INTENT"
                        );
                      } else if (newPaymentMethod?.value === "UPI_COLLECT") {
                        filtered = filtered.filter(
                          (p) => p.mode === "UPI_COLLECT"
                        );
                      }

                      return [
                        {
                          label: "Payin",
                          options: filtered.map((p) => ({
                            label: `${p.providerName} - ${p.orgName}`,
                            value: p.id,
                            providerId: p.id,
                            providerName: p.providerName,
                            orgName: p.orgName,
                          })),
                        },
                      ];
                    })()}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </td>
                <td>
                  <Select
                    value={newPaymentMethod}
                    onChange={(selected) => setNewPaymentMethod(selected)}
                    options={[
                      { label: "UPI_INTENT", value: "UPI_INTENT" },
                      { label: "UPI_COLLECT", value: "UPI_COLLECT" },
                    ]}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </td>
                <td>-</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {(openId || isAdding) && (!loading)  && (
        <div
          style={{
            display: "flex",
            justifyContent: "right",
            gap: "100px",
            marginTop: "10px",
          }}
        >
          <button
            className="btn-apply"
            onClick={() =>
              isAdding ? handleAddSwitch() : handleUpdateAutoSwitch()
            }
          >
            {isAdding ? "Add" : "Apply"}
          </button>
          <button className="btn-revert" onClick={handleClose}>
            Cancel
          </button>
        </div>
      )}
    </>
  );
};

export default MerchantDetailTable;
